import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'
import { AuthProvider } from '@/hooks/useAuth'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { AdminRoute } from '@/components/AdminRoute'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { MainLayout } from '@/components/layout/MainLayout'

const LandingPage         = lazy(() => import('@/pages/LandingPage').then(m => ({ default: m.LandingPage })))
const AdminPage           = lazy(() => import('@/pages/AdminPage').then(m => ({ default: m.AdminPage })))
const LoginPage           = lazy(() => import('@/pages/LoginPage').then(m => ({ default: m.LoginPage })))
const RegisterPage        = lazy(() => import('@/pages/RegisterPage').then(m => ({ default: m.RegisterPage })))
const ForgotPasswordPage  = lazy(() => import('@/pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })))
const VerifyEmailPage     = lazy(() => import('@/pages/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })))
const ResetPasswordPage   = lazy(() => import('@/pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })))
const DashboardPage       = lazy(() => import('@/pages/DashboardPage').then(m => ({ default: m.DashboardPage })))
const ProjectsPage        = lazy(() => import('@/pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })))
const ProjectDetailPage   = lazy(() => import('@/pages/ProjectDetailPage').then(m => ({ default: m.ProjectDetailPage })))
const ReportsPage         = lazy(() => import('@/pages/ReportsPage').then(m => ({ default: m.ReportsPage })))
const SettingsPage        = lazy(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const HelpPage            = lazy(() => import('@/pages/HelpPage').then(m => ({ default: m.HelpPage })))
const TermsPage           = lazy(() => import('@/pages/TermsPage').then(m => ({ default: m.TermsPage })))
const PrivacyPolicyPage   = lazy(() => import('@/pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })))
const NewProjectWizard    = lazy(() => import('@/pages/NewProjectWizard').then(m => ({ default: m.NewProjectWizard })))
const QueryGenerationPage = lazy(() => import('@/pages/QueryGenerationPage').then(m => ({ default: m.QueryGenerationPage })))
const SearchExecutionPage = lazy(() => import('@/pages/SearchExecutionPage').then(m => ({ default: m.SearchExecutionPage })))
const PatentAnalysisPage  = lazy(() => import('@/pages/PatentAnalysisPage').then(m => ({ default: m.PatentAnalysisPage })))
const AnalysisResultsPage = lazy(() => import('@/pages/AnalysisResultsPage').then(m => ({ default: m.AnalysisResultsPage })))
const ReportGenerationPage= lazy(() => import('@/pages/ReportGenerationPage').then(m => ({ default: m.ReportGenerationPage })))
const LandscapeSummaryPage= lazy(() => import('@/pages/LandscapeSummaryPage').then(m => ({ default: m.LandscapeSummaryPage })))
const WatchPage           = lazy(() => import('@/pages/WatchPage').then(m => ({ default: m.WatchPage })))
const NotFoundPage        = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })))

function App() {
  return (
    <AuthProvider>
      <Suspense fallback={null}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />

        {/* Protected routes with layout */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout>
                <ErrorBoundary>
                  <Outlet />
                </ErrorBoundary>
              </MainLayout>
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />

          {/* Project routes */}
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/new" element={<NewProjectWizard />} />
          <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
          <Route path="/projects/:projectId/edit" element={<NewProjectWizard />} />

          {/* Project workflow routes (Steps 1-5) */}
          <Route path="/projects/:projectId/queries" element={<QueryGenerationPage />} />
          <Route path="/projects/:projectId/search" element={<SearchExecutionPage />} />
          <Route path="/projects/:projectId/analysis" element={<PatentAnalysisPage />} />
          <Route path="/projects/:projectId/results" element={<AnalysisResultsPage />} />
          <Route path="/projects/:projectId/report" element={<ReportGenerationPage />} />
          <Route path="/projects/:projectId/landscape" element={<LandscapeSummaryPage />} />
          <Route path="/projects/:projectId/watch" element={<WatchPage />} />

          {/* Standalone routes */}
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminPage />
              </AdminRoute>
            }
          />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Toaster />
      </Suspense>
    </AuthProvider>
  )
}

export default App
